import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Database,
  Users,
  Pencil,
  ChevronDown,
} from 'lucide-react';
import clsx from 'clsx';
import type {
  AnalyticsAddon,
  ClientProfile,
  Dimension,
  GeoModel,
  LevelMixOverride,
  Model,
  OgsConfig,
  OgsDisciplineConfig,
  StaffPlan,
} from '@rc/engine/types';
import {
  currencySymbol,
  deriveBudgetBucketId,
  deriveLobCountId,
  flattenStaffPlanToByLevel,
  isActualStaffPlan,
  isOgsEligible,
  modelCurrency,
  staffPlanEntrySource,
  staffPlanEntrySourceLabel,
  staffPlanHorizonWords,
} from '@rc/engine';
import { fmtMoney, fmtPct } from '../lib/format';
import { api } from '../lib/api';
import type { ModelSummary } from '../lib/api';
import { withGeoclientQuery } from '../lib/geoclientContext';
import LobList from './intake/LobList';
import CountryPicker from './intake/CountryPicker';
import GeoPairsEditor from './intake/GeoPairsEditor';
import OgsDisciplineRow from './intake/OgsCard';
import LevelMixRow from './intake/LevelMixCard';
import PresetBar from './intake/PresetBar';
import AutomationSolutionSetCard from './intake/AutomationSolutionSetCard';

function parseNumberInput(raw: string): number {
  if (typeof raw !== 'string') return 0;
  const cleaned = raw.replace(/[^0-9.\-]/g, '');
  const n = Number(cleaned);
  return isFinite(n) ? n : 0;
}

interface Props {
  model: Model;
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
  /** Full catalog of models the scenario can be repointed at (summaries are
   *  enough — the picker only needs id/name). Required to render the Model
   *  picker card; falls back to a read-only display when omitted. */
  models?: ModelSummary[];
  /** Invoked with the picked model's id when the user selects a different
   *  model. The consumer is expected to fetch the full model, swap the
   *  engine model, and recalc (typically via `api.getModel` +
   *  `useScenarioStore.setModel`). */
  onModelChange?: (modelId: string) => void;
  /** True while a model switch is in-flight (fetching the full model).
   *  Disables the picker so the user can't fire overlapping switches. */
  modelSwitching?: boolean;
  /** Optional — when provided, enables links to the dedicated staff-plan editor. */
  clientId?: string;
  scenarioId?: string;
  geoclientId?: string;
}

type InputsTab = 'client_profile' | 'ogs' | 'leveling';

export default function IntakeForm({
  model,
  profile,
  onChange,
  models,
  onModelChange,
  modelSwitching,
  clientId,
  scenarioId,
  geoclientId,
}: Props) {
  const [tab, setTab] = useState<InputsTab>('client_profile');

  return (
    <>
      <ModelPickerCard
        model={model}
        models={models}
        onModelChange={onModelChange}
        switching={modelSwitching}
      />

      <InputsTabBar value={tab} onChange={setTab} />

      {tab === 'client_profile' && (
        <>
          <Card1ClientScope model={model} profile={profile} onChange={onChange} />
          <Card2Vertical model={model} profile={profile} onChange={onChange} />
          <Card3BudgetChannels model={model} profile={profile} onChange={onChange} clientId={clientId} />
          <Card4CampaignProfile model={model} profile={profile} onChange={onChange} />
          <Card5Geography profile={profile} onChange={onChange} />
          <Card7AnalyticsAddons profile={profile} onChange={onChange} />
          <CardStaffPlan
            profile={profile}
            onChange={onChange}
            clientId={clientId}
            scenarioId={scenarioId}
            geoclientId={geoclientId}
          />
          <AutomationSolutionSetCard
            profile={profile}
            onChange={onChange}
            clientId={clientId}
            geoclientId={geoclientId}
            modelId={model.id}
          />
        </>
      )}
      {tab === 'ogs' && <Card6Ogs model={model} profile={profile} onChange={onChange} />}
      {tab === 'leveling' && (
        <CardLeveling model={model} profile={profile} onChange={onChange} />
      )}
    </>
  );
}

// Tab switcher for the Inputs panel — separates the Client profile inputs from
// the OGS configuration so each has room to breathe.
function InputsTabBar({
  value,
  onChange,
}: {
  value: InputsTab;
  onChange: (t: InputsTab) => void;
}) {
  const tabs: { id: InputsTab; label: string }[] = [
    { id: 'client_profile', label: 'Client profile' },
    { id: 'leveling', label: 'Leveling' },
    { id: 'ogs', label: 'OGS' },
  ];
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-1 inline-flex shadow-sm">
      {tabs.map((t) => {
        const active = value === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={clsx(
              'px-4 py-2 rounded-md text-xs font-medium transition-all',
              active
                ? 'bg-brand-purple-50 text-brand-purple-700 ring-1 ring-brand-purple-200 shadow-sm'
                : 'text-slate-500 hover:text-slate-800 hover:bg-slate-50',
            )}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

function CardShell({
  num,
  title,
  description,
  children,
}: {
  num: number | string;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="card">
      <div className="card-header">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center text-xs font-semibold">
            {num}
          </div>
          <div>
            <h3 className="text-sm font-semibold">{title}</h3>
            <p className="text-xs text-slate-500">{description}</p>
          </div>
        </div>
      </div>
      <div className="card-body space-y-4">{children}</div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Model picker — sits at the top of the Inputs panel so the user can repoint
// the scenario at a different model (e.g. swap Global → US) without leaving
// the editor. Read-only when no `models` list / `onModelChange` is provided.
// ---------------------------------------------------------------------------
function ModelPickerCard({
  model,
  models,
  onModelChange,
  switching,
}: {
  model: Model;
  models?: ModelSummary[];
  onModelChange?: (modelId: string) => void;
  switching?: boolean;
}) {
  const canPick =
    !!onModelChange && Array.isArray(models) && models.length > 0;

  // Always include the active model in the options, even if it isn't in the
  // injected list yet (e.g. still loading). Avoids a flash of "—" in the
  // <select> on mount.
  const options = (() => {
    const list = (models || []).slice();
    if (!list.some((m) => m.id === model.id)) list.unshift(model);
    return list.sort((a, b) => a.name.localeCompare(b.name));
  })();

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!onModelChange) return;
    const nextId = e.target.value;
    if (nextId !== model.id) onModelChange(nextId);
  };

  return (
    <section className="card">
      <div className="card-body py-3">
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center shrink-0">
            <Database size={14} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Model
            </div>
            <div className="text-sm font-semibold text-slate-800 truncate">
              {model.name}
            </div>
          </div>
          {canPick && (
            <select
              value={model.id}
              onChange={handleChange}
              disabled={switching}
              className="border border-slate-300 rounded-md px-2.5 py-1.5 text-sm bg-white hover:border-slate-400 focus:border-brand-purple-400 focus:ring-1 focus:ring-brand-purple-200 outline-none min-w-[180px] disabled:opacity-60 disabled:cursor-wait"
              title="Switch the model this scenario uses"
            >
              {options.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          )}
        </div>
        {canPick && (
          <p className="text-[11px] text-slate-500 mt-2 pl-10">
            {switching
              ? 'Switching model…'
              : 'Switching the model swaps the backbone tables (Spend per FTE, ' +
                'Level Weights, Cost per Role, Hub %, Time Concentration). Inputs ' +
                'stay the same; the result will recalculate.'}
          </p>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Card 1 — Client & Scope
// ---------------------------------------------------------------------------
function Card1ClientScope({
  model,
  profile,
  onChange,
}: {
  model: Model;
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
}) {
  const lobCount = (profile.lobs || []).length;
  const lobBucketId = deriveLobCountId(model, lobCount);
  const lobDim = (model.dimensions || []).find((d) => d.id === 'lob_count');
  const lobBucket = lobDim?.options.find((o) => o.value === lobBucketId);
  const lobBucketDescription =
    lobBucket && typeof lobBucket.factor === 'number'
      ? `${lobBucket.label} · ×${lobBucket.factor.toFixed(2)}`
      : lobBucket?.label;

  return (
    <CardShell num={1} title="Client & Scope" description="Parent brand, LOBs / sub-brands, and the markets the scenario covers.">
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="label">LOBs / Sub-brands</label>
          <LobList
            value={profile.lobs || []}
            onChange={(lobs) => onChange({ lobs })}
            bucketLabel={lobBucketDescription}
          />
          {lobDim && (
            <p className="text-xs text-slate-400 mt-1">
              The LOB count maps to a bucketed factor on the model
              (Model → Profile factors → <strong>LOB / Sub-brand Count</strong>).
            </p>
          )}
        </div>
        <div>
          <label className="label">Scenario markets</label>
          <CountryPicker
            value={profile.scenarioMarkets || []}
            onChange={(scenarioMarkets) => onChange({ scenarioMarkets })}
            placeholder="Select markets the scenario covers…"
          />
          <p className="text-xs text-slate-400 mt-1">Used to scope geography modeling and reporting.</p>
        </div>
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Card 2 — Vertical
// ---------------------------------------------------------------------------
function Card2Vertical({
  model,
  profile,
  onChange,
}: {
  model: Model;
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
}) {
  const dim = (model.dimensions || []).find((d) => d.id === 'vertical');
  if (!dim) return null;
  const value = profile.selections?.vertical || '';

  return (
    <CardShell num={2} title="Vertical" description="Client vertical drives discipline-specific factors set in the Data Model.">
      <div>
        <label className="label">Vertical</label>
        <select
          className="input"
          value={value}
          onChange={(e) =>
            onChange({ selections: { ...profile.selections, vertical: e.target.value } })
          }
        >
          <option value="">Select vertical…</option>
          {dim.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Card 3 — Annual Working Media & Channels
// ---------------------------------------------------------------------------
function Card3BudgetChannels({
  model,
  profile,
  onChange,
  clientId,
}: {
  model: Model;
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
  clientId?: string;
}) {
  const [spendMode, setSpendMode] = useState<'manual' | 'saved'>(
    profile.currentSpendSetId ? 'saved' : 'manual',
  );
  const [spendSets, setSpendSets] = useState<import('@rc/engine/types').SpendSet[]>([]);
  const [loadingSpendSets, setLoadingSpendSets] = useState(false);
  const [loadingSet, setLoadingSet] = useState(false);
  // Budgets are entered in the model's own currency — the engine never converts.
  const baseCurrency = modelCurrency(model).base;

  // Load saved spend sets for this client when picker mode is activated.
  useEffect(() => {
    if (spendMode !== 'saved' || !clientId) return;
    let cancelled = false;
    setLoadingSpendSets(true);
    api.listSpendSets(clientId)
      .then((list) => { if (!cancelled) setSpendSets(list); })
      .catch(() => { if (!cancelled) setSpendSets([]); })
      .finally(() => { if (!cancelled) setLoadingSpendSets(false); });
    return () => { cancelled = true; };
  }, [spendMode, clientId]);

  async function applySpendSet(id: string) {
    if (!clientId || !id) {
      onChange({ currentSpendSetId: undefined });
      return;
    }
    setLoadingSet(true);
    try {
      const set = await api.getSpendSet(clientId, id);
      onChange({
        currentSpendSetId: set.id,
        totalBudget: set.totalBudget,
        channelAllocation: set.channelAllocation,
        channelMixPresetId: set.channelMixPresetId,
        activeChannels: set.activeChannels,
        adopsSupport: set.adopsSupport,
      });
    } catch {
      // ignore — user can still edit manually
    } finally {
      setLoadingSet(false);
    }
  }

  function switchToManual() {
    setSpendMode('manual');
    onChange({ currentSpendSetId: undefined });
  }
  const totalAlloc =
    Object.values(profile.channelAllocation || {}).reduce<number>(
      (s, v) => s + (Number(v) || 0),
      0,
    ) || 0;
  const allocOff = Math.abs(totalAlloc - 1) > 0.005;
  const channels = model.channels.filter((c) => c.active !== false);
  const totalBudget = Number(profile.totalBudget) || 0;
  const bucketId = deriveBudgetBucketId(model, totalBudget);
  const bucket = (model.budgetBuckets || []).find((b) => b.id === bucketId);

  const updateBudget = (n: number) => {
    const next: Partial<ClientProfile> = { totalBudget: n };
    const derived = deriveBudgetBucketId(model, n);
    if (derived) {
      next.selections = { ...profile.selections, budget_bucket: derived };
    }
    onChange(next);
  };

  const updatePreset = (id: string) => {
    const preset = model.channelMixPresets.find((p) => p.id === id);
    if (!preset || !preset.allocation) {
      onChange({ channelMixPresetId: id });
      return;
    }
    const evenShare = channels.length > 0 ? 1 / channels.length : 0;
    const allocation: Record<string, number> = {};
    for (const ch of channels) allocation[ch.id] = preset.allocation[ch.id] ?? evenShare;
    onChange({ channelMixPresetId: id, channelAllocation: allocation });
  };

  const toggleChannel = (id: string, on: boolean) => {
    onChange({ activeChannels: { ...profile.activeChannels, [id]: on } });
  };

  const updateAllocation = (id: string, v: number) => {
    onChange({
      channelAllocation: { ...profile.channelAllocation, [id]: v },
      channelMixPresetId: 'custom',
    });
  };
  const updateAllocationPct = (id: string, pct: number) => {
    const share = isFinite(pct) ? pct / 100 : 0;
    updateAllocation(id, Math.max(0, share));
  };
  const updateAllocationDollars = (id: string, dollars: number) => {
    const budget = Number(profile.totalBudget) || 0;
    if (budget <= 0) {
      onChange({
        totalBudget: Math.max(0, dollars),
        channelAllocation: { ...profile.channelAllocation, [id]: dollars > 0 ? 1 : 0 },
        channelMixPresetId: 'custom',
      });
      return;
    }
    const share = Math.max(0, dollars) / budget;
    updateAllocation(id, share);
  };

  const toggleAdops = (id: string, on: boolean) => {
    onChange({ adopsSupport: { ...profile.adopsSupport, [id]: on } });
  };

  return (
    <CardShell
      num={3}
      title="Annual working media & channels"
      description="Total media spend and the channel allocation that drives investment + AdOps staffing."
    >
      {clientId && (
        <div className="flex items-center gap-2 mb-3">
          <span className="text-xs text-slate-500 font-medium shrink-0">Spend input:</span>
          <div className="flex rounded-lg border border-slate-200 overflow-hidden text-xs">
            <button
              type="button"
              onClick={switchToManual}
              className={clsx(
                'px-3 py-1.5 font-medium transition-colors',
                spendMode === 'manual'
                  ? 'bg-brand-purple-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              Enter manually
            </button>
            <button
              type="button"
              onClick={() => setSpendMode('saved')}
              className={clsx(
                'px-3 py-1.5 font-medium transition-colors border-l border-slate-200',
                spendMode === 'saved'
                  ? 'bg-brand-purple-600 text-white'
                  : 'bg-white text-slate-600 hover:bg-slate-50',
              )}
            >
              Load from saved set
            </button>
          </div>
          {spendMode === 'saved' && (
            <div className="flex-1 min-w-0">
              {loadingSpendSets ? (
                <span className="text-xs text-slate-400">Loading…</span>
              ) : spendSets.length === 0 ? (
                <span className="text-xs text-slate-400">No saved spend sets for this client.</span>
              ) : (
                <select
                  className="input py-1 text-xs"
                  value={profile.currentSpendSetId || ''}
                  onChange={(e) => applySpendSet(e.target.value)}
                  disabled={loadingSet}
                >
                  <option value="">— Select a spend set —</option>
                  {spendSets.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}
          {profile.currentSpendSetId && spendMode === 'saved' && (
            <span className="text-[10px] pill bg-emerald-50 text-emerald-700 shrink-0">
              Spend set applied
            </span>
          )}
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="label">
            Annual working media
            <span className="ml-1.5 font-normal text-slate-400">({baseCurrency})</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">
              {currencySymbol(baseCurrency)}
            </span>
            <input
              className="input pl-9 text-right tabular-nums"
              type="text"
              inputMode="numeric"
              value={totalBudget.toLocaleString('en-US')}
              onChange={(e) => updateBudget(parseNumberInput(e.target.value))}
            />
          </div>
          <p className="text-xs text-slate-400 mt-1 flex items-center gap-2">
            {fmtMoney(totalBudget, baseCurrency)} total
            {bucket && (
              <span className="pill bg-brand-purple-50 text-brand-purple-700 text-[10px]">
                {bucket.label}
              </span>
            )}
          </p>
        </div>
        <div>
          <label className="label">Channel mix preset</label>
          <select
            className="input"
            value={profile.channelMixPresetId || 'custom'}
            onChange={(e) => updatePreset(e.target.value)}
          >
            {model.channelMixPresets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <span className="label !mb-0">Active channels & allocation</span>
          <span
            className={clsx(
              'text-xs font-medium',
              allocOff ? 'text-rose-600' : 'text-emerald-600',
            )}
          >
            {fmtPct(totalAlloc, 1)} {allocOff && '(should be 100%)'}
          </span>
        </div>
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Channel</th>
                <th className="text-right px-3 py-2 w-28">Share %</th>
                <th className="text-right px-3 py-2 w-36">Dollars</th>
                <th className="text-center px-3 py-2 w-20">AdOps</th>
              </tr>
            </thead>
            <tbody>
              {channels.map((c) => {
                const active = profile.activeChannels?.[c.id] !== false;
                const share = profile.channelAllocation?.[c.id] || 0;
                const dollars = totalBudget * share;
                const pctValue = Number((share * 100).toFixed(2));
                const dollarValue = Math.round(dollars);
                return (
                  <tr
                    key={c.id}
                    className={clsx('border-t border-slate-100', !active && 'opacity-50')}
                  >
                    <td className="px-3 py-2">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 text-brand-purple-600"
                          checked={active}
                          onChange={(e) => toggleChannel(c.id, e.target.checked)}
                        />
                        <span>{c.label}</span>
                        <span
                          className={c.category === 'traditional' ? 'pill-slate' : 'pill-blue'}
                        >
                          {c.category[0]?.toUpperCase()}
                        </span>
                      </label>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="relative w-24 ml-auto">
                        <input
                          type="number"
                          step="0.1"
                          min={0}
                          max={100}
                          className="input text-right py-1 pl-2 pr-6 w-full"
                          value={pctValue}
                          onChange={(e) => updateAllocationPct(c.id, Number(e.target.value))}
                          disabled={!active}
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                          %
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="relative w-36 ml-auto">
                        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 text-xs">
                          {currencySymbol(baseCurrency)}
                        </span>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="input text-right py-1 pl-7 pr-2 w-full tabular-nums"
                          value={dollarValue.toLocaleString('en-US')}
                          onChange={(e) =>
                            updateAllocationDollars(c.id, parseNumberInput(e.target.value))
                          }
                          disabled={!active}
                          title={fmtMoney(dollars, baseCurrency)}
                        />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center">
                      {c.adopsApplicable ? (
                        <input
                          type="checkbox"
                          className="rounded border-slate-300 text-brand-purple-600"
                          checked={profile.adopsSupport?.[c.id] !== false}
                          onChange={(e) => toggleAdops(c.id, e.target.checked)}
                          disabled={!active}
                        />
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Card 4 — Campaign Profile
// ---------------------------------------------------------------------------
const CAMPAIGN_PROFILE_DIM_IDS = [
  'campaigns_per_year',
  'complexity_objective',
  'complexity_tracking',
  'complexity_velocity',
  'complexity_creative',
  'complexity_stakeholders',
  'audience_sources',
] as const;

function MultiSelectDropdown({
  label,
  description,
  options,
  selected,
  onChange,
}: {
  label: string;
  description?: string;
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const summary =
    selected.length === 0
      ? 'Select…'
      : options
          .filter((o) => selected.includes(o.value))
          .map((o) => o.label)
          .join(', ');

  const toggle = (value: string) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  return (
    <div ref={rootRef} className="relative">
      <label className="label">{label}</label>
      <button
        type="button"
        className="input w-full text-left flex items-center justify-between gap-2"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={clsx('truncate', selected.length === 0 && 'text-slate-400')}>{summary}</span>
        <ChevronDown size={14} className="text-slate-400 shrink-0" />
      </button>
      {open && (
        <div
          role="listbox"
          aria-multiselectable
          className="absolute z-20 mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg py-1 max-h-56 overflow-auto"
        >
          {options.map((o) => {
            const on = selected.includes(o.value);
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={on}
                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                onClick={() => toggle(o.value)}
              >
                <span
                  className={clsx(
                    'inline-flex h-4 w-4 items-center justify-center rounded border text-[10px]',
                    on
                      ? 'bg-brand-purple-600 border-brand-purple-600 text-white'
                      : 'border-slate-300 bg-white text-transparent',
                  )}
                >
                  ✓
                </span>
                {o.label}
              </button>
            );
          })}
        </div>
      )}
      {description && <p className="text-xs text-slate-400 mt-1">{description}</p>}
    </div>
  );
}

function Card4CampaignProfile({
  model,
  profile,
  onChange,
}: {
  model: Model;
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
}) {
  const dims = (model.dimensions || []).filter((d) =>
    (CAMPAIGN_PROFILE_DIM_IDS as readonly string[]).includes(d.id),
  );
  if (dims.length === 0) return null;

  const setSel = (id: string, value: string | string[]) =>
    onChange({ selections: { ...profile.selections, [id]: value } });

  return (
    <CardShell
      num={4}
      title="Campaign profile"
      description="Campaign volume, portfolio complexity, creative/stakeholder load, and audience sources."
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {dims.map((d: Dimension) => {
          if (d.kind === 'multiselect') {
            const current = profile.selections?.[d.id];
            const selected = Array.isArray(current)
              ? current
              : typeof current === 'string' && current
                ? [current]
                : [];
            return (
              <MultiSelectDropdown
                key={d.id}
                label={d.label}
                description={d.description}
                options={d.options}
                selected={selected}
                onChange={(next) => setSel(d.id, next)}
              />
            );
          }

          const value =
            typeof profile.selections?.[d.id] === 'string'
              ? (profile.selections[d.id] as string)
              : '';
          return (
            <div key={d.id}>
              <label className="label">{d.label}</label>
              <select
                className="input"
                value={value}
                onChange={(e) => setSel(d.id, e.target.value)}
              >
                <option value="">Select…</option>
                {d.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {d.description && <p className="text-xs text-slate-400 mt-1">{d.description}</p>}
            </div>
          );
        })}
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Card 5 — Geography & Hubs
// ---------------------------------------------------------------------------
function Card5Geography({
  profile,
  onChange,
}: {
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
}) {
  const geo: GeoModel = profile.geoModel || {
    type: 'single',
    pairs: [{ workingLocation: 'US', activationMarkets: profile.scenarioMarkets || [] }],
  };
  return (
    <CardShell
      num={5}
      title="Geography & hubs"
      description="Where the agency team sits, and which markets they activate into."
    >
      <GeoPairsEditor value={geo} onChange={(geoModel) => onChange({ geoModel })} />
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Card 6 — OGS configuration
// ---------------------------------------------------------------------------
const ANALYTICS_ADDON_LABELS: Record<AnalyticsAddon, string> = {
  data_viz_reporting: 'Data Visualization & Reporting',
  marketing_science: 'Marketing Science',
  audience: 'Audience',
};

function Card6Ogs({
  model,
  profile,
  onChange,
}: {
  model: Model;
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
}) {
  const ogs: OgsConfig = profile.ogs || {};
  const channelIdsFor = (discId: string) => {
    const ids = Array.from(
      new Set((model.spendPerFte || []).filter((r) => r.disciplineId === discId).map((r) => r.channelId)),
    );
    return ids.length > 0 ? ids : ['all'];
  };
  const disciplines = (model.disciplines || [])
    .filter((d) => d.active !== false)
    .filter((d) => channelIdsFor(d.id).some((ch) => isOgsEligible(model, d.id, ch)));
  const hasHubs = (model.ogsHubs || []).length > 0;
  const allDisabled =
    hasHubs && (model.disciplines || []).filter((d) => d.active !== false).length > 0 && disciplines.length === 0;

  const disciplineConfigs = ogs.disciplines || [];
  const presets = model.ogsPresets || [];

  const setDisciplineConfig = (next: OgsDisciplineConfig) => {
    const nextCh = next.channelId ?? 'all';
    const idx = disciplineConfigs.findIndex(
      (d) => d.disciplineId === next.disciplineId && (d.channelId ?? 'all') === nextCh,
    );
    const nextList =
      idx >= 0
        ? disciplineConfigs.map((d, i) => (i === idx ? next : d))
        : [...disciplineConfigs, next];
    // Hand-tuning a slider detaches from the applied preset.
    onChange({ ogs: { ...ogs, disciplines: nextList }, ogsPresetId: 'custom' });
  };

  const applyPreset = (presetId: string) => {
    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return;
    onChange({
      ogs: { ...ogs, disciplines: preset.disciplines.map((d) => ({ ...d })) },
      ogsPresetId: preset.id,
    });
  };

  return (
    <CardShell
      num={6}
      title="OGS"
      description="Optional Global Services — enable per discipline, pick the hub, set the % and which roles it covers."
    >
      <PresetBar
        presets={presets}
        activeId={profile.ogsPresetId}
        onApply={applyPreset}
        onCustom={() => onChange({ ogsPresetId: 'custom' })}
      />
      {!hasHubs ? (
        <p className="text-xs text-amber-700">
          This model has no OGS hubs configured, so OGS cannot be enabled.
        </p>
      ) : allDisabled ? (
        <p className="text-xs text-slate-500">
          No disciplines are enabled for OGS in this model. Turn rows on under
          Model → Hub locations % to offer them here.
        </p>
      ) : (
        <div className="space-y-2">
          {disciplines.map((d) => (
            <OgsDisciplineRow
              key={d.id}
              discipline={d}
              model={model}
              configs={disciplineConfigs}
              onChange={setDisciplineConfig}
            />
          ))}
        </div>
      )}
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Leveling — per-discipline seniority tilt
// ---------------------------------------------------------------------------
function CardLeveling({
  model,
  profile,
  onChange,
}: {
  model: Model;
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
}) {
  const disciplines = (model.disciplines || []).filter((d) => d.active !== false);
  const levelMix = profile.levelMix || [];
  const presets = model.levelMixPresets || [];

  const setLevelMix = (next: LevelMixOverride) => {
    const nextCh = next.channelId ?? 'all';
    const idx = levelMix.findIndex(
      (e) => e.disciplineId === next.disciplineId && (e.channelId ?? 'all') === nextCh,
    );
    let nextList: LevelMixOverride[];
    if (next.shift === 0) {
      // Clearing to Balanced removes the override entirely.
      nextList = levelMix.filter((_, i) => i !== idx);
    } else if (idx >= 0) {
      nextList = levelMix.map((e, i) => (i === idx ? next : e));
    } else {
      nextList = [...levelMix, next];
    }
    // Hand-tuning a slider detaches from the applied preset.
    onChange({ levelMix: nextList, levelMixPresetId: 'custom' });
  };

  const applyPreset = (presetId: string) => {
    const preset = presets.find((p) => p.id === presetId);
    if (!preset) return;
    onChange({
      levelMix: preset.overrides.map((o) => ({ ...o })),
      levelMixPresetId: preset.id,
    });
  };

  return (
    <CardShell
      num="↕"
      title="Leveling"
      description="Marginally shift each discipline's seniority mix toward junior or executive. Total FTE stays the same — only the level composition changes."
    >
      <PresetBar
        presets={presets}
        activeId={profile.levelMixPresetId}
        onApply={applyPreset}
        onCustom={() => onChange({ levelMixPresetId: 'custom' })}
      />
      {disciplines.length === 0 ? (
        <p className="text-xs text-slate-400">This model has no active disciplines.</p>
      ) : (
        <div className="space-y-2">
          {disciplines.map((d) => (
            <LevelMixRow
              key={d.id}
              discipline={d}
              model={model}
              configs={levelMix}
              onChange={setLevelMix}
            />
          ))}
        </div>
      )}
    </CardShell>
  );
}

function Card7AnalyticsAddons({
  profile,
  onChange,
}: {
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
}) {
  const addons = profile.analyticsAddons || [];

  const toggleAddon = (id: AnalyticsAddon, on: boolean) => {
    const next: AnalyticsAddon[] = on
      ? Array.from(new Set([...addons, id]))
      : addons.filter((a) => a !== id);
    onChange({ analyticsAddons: next });
  };

  return (
    <CardShell
      num={7}
      title="Analytics add-ons"
      description="Toggle the Analytics sub-disciplines included in this engagement."
    >
      <div>
        <label className="label">Analytics add-ons</label>
        <div className="flex flex-wrap gap-2">
          {(Object.keys(ANALYTICS_ADDON_LABELS) as AnalyticsAddon[]).map((id) => {
            const on = addons.includes(id);
            return (
              <button
                key={id}
                type="button"
                onClick={() => toggleAddon(id, !on)}
                className={clsx(
                  'pill border transition-colors',
                  on
                    ? 'bg-brand-purple-50 text-brand-purple-700 border-brand-purple-200'
                    : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300',
                )}
              >
                {ANALYTICS_ADDON_LABELS[id]}
              </button>
            );
          })}
          {addons.length === 0 && (
            <span className="text-xs text-slate-400 self-center">
              None selected — no analytics sub-disciplines will be staffed.
            </span>
          )}
        </div>
      </div>
    </CardShell>
  );
}

// ---------------------------------------------------------------------------
// Current staff plan — summary card with a button to the dedicated editor
// ---------------------------------------------------------------------------
function CardStaffPlan({
  profile,
  onChange,
  clientId,
  scenarioId,
  geoclientId,
}: {
  profile: ClientProfile;
  onChange: (patch: Partial<ClientProfile>) => void;
  clientId?: string;
  scenarioId?: string;
  geoclientId?: string;
}) {
  const linkedPlanId = profile.currentStaffPlanId;
  const [plan, setPlan] = useState<StaffPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState<StaffPlan[]>([]);
  const [attaching, setAttaching] = useState(false);
  const [attachError, setAttachError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!clientId || !linkedPlanId) {
      setPlan(null);
      return;
    }
    setLoading(true);
    api
      .getStaffPlan(clientId, linkedPlanId)
      .then((p) => {
        if (!cancelled) setPlan(p);
      })
      .catch(() => {
        if (!cancelled) setPlan(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, linkedPlanId]);

  // The client's saved plans, so a scenario can reuse one instead of re-entering
  // the same team. Failure is quiet: the picker just doesn't appear, and the
  // editor link still works.
  useEffect(() => {
    let cancelled = false;
    if (!clientId) {
      setSaved([]);
      return;
    }
    api
      .listStaffPlans(clientId)
      .then((list) => {
        if (!cancelled) setSaved(list || []);
      })
      .catch(() => {
        if (!cancelled) setSaved([]);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  /**
   * Point the scenario at a saved plan.
   *
   * The list endpoint returns metadata without cells, so the full plan is
   * fetched to rebuild `currentStaffByLevel` — the flattened view the engine
   * actually compares against. This mirrors what the staff-plan editor does on
   * save, keeping one definition of "attached".
   */
  const attach = async (id: string) => {
    if (!clientId || attaching) return;
    setAttaching(true);
    setAttachError(null);
    try {
      const full = await api.getStaffPlan(clientId, id);
      onChange({
        currentStaffPlanId: full.id,
        currentStaffByLevel: flattenStaffPlanToByLevel(full.cells || []),
        currentStaffByDiscipline: {},
      });
    } catch (err) {
      setAttachError(err instanceof Error ? err.message : 'Could not load that plan');
    } finally {
      setAttaching(false);
    }
  };

  const detach = () => {
    onChange({
      currentStaffPlanId: undefined,
      currentStaffByLevel: {},
      currentStaffByDiscipline: {},
    });
    setAttachError(null);
  };

  const totalFromMap = Object.values(profile.currentStaffByLevel || {}).reduce<number>(
    (s, v) => s + (Number(v) || 0),
    0,
  );
  const totalDisc = Object.values(profile.currentStaffByDiscipline || {}).reduce<number>(
    (s, v) => s + (Number(v) || 0),
    0,
  );
  const total = totalFromMap || totalDisc;
  const cellCount = Object.keys(profile.currentStaffByLevel || {}).filter(
    (k) => (profile.currentStaffByLevel as Record<string, number>)[k] > 0,
  ).length;

  const editHref = clientId
    ? withGeoclientQuery(
        `/clients/${clientId}/staff-plans/${linkedPlanId || 'new'}` +
          (scenarioId ? `?from=${scenarioId}` : ''),
        geoclientId,
      )
    : null;

  return (
    <CardShell
      num="•"
      title="Current staff plan"
      description="The team you have today, used to compare against the modeled plan."
    >
      {linkedPlanId ? (
        <div className="rounded-lg border border-slate-200 px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-brand-purple-50 text-brand-purple-700 flex items-center justify-center shrink-0">
            <Users size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-800 flex items-center gap-2 min-w-0">
              <span className="truncate">
                {loading ? 'Loading plan…' : plan?.name || 'Current staff plan'}
              </span>
              {plan && <PlanKindPill plan={plan} />}
            </div>
            <div className="text-xs text-slate-500 truncate">
              <span className="tabular-nums font-medium text-slate-700">{total.toFixed(1)}</span>{' '}
              total FTE
              {cellCount > 0 && (
                <>
                  {' · '}
                  <span className="tabular-nums">{cellCount}</span> cells
                </>
              )}
              {plan?.updatedAt && (
                <>
                  {' · '}
                  updated {new Date(plan.updatedAt).toLocaleDateString()}
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StaffPlanPicker
              plans={saved}
              linkedId={linkedPlanId}
              busy={attaching}
              label="Change"
              onPick={attach}
              onClear={detach}
            />
            {editHref && (
              <Link to={editHref} className="btn-secondary shrink-0">
                <Pencil size={14} /> Edit plan
              </Link>
            )}
          </div>
        </div>
      ) : total > 0 ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
            <Users size={16} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-amber-900">
              Legacy inline staff data ({total.toFixed(1)} FTE)
            </div>
            <div className="text-xs text-amber-700">
              Open the new editor to convert these values into a named, reusable Current Staff Plan.
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StaffPlanPicker
              plans={saved}
              linkedId={linkedPlanId}
              busy={attaching}
              label="Use a saved plan"
              onPick={attach}
            />
            {editHref && (
              <Link to={editHref} className="btn-primary shrink-0">
                <Pencil size={14} /> Convert
              </Link>
            )}
          </div>
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center">
          <Users size={20} className="text-slate-400 mx-auto mb-2" />
          <div className="text-sm font-medium text-slate-700">No current staff plan yet</div>
          <div className="text-xs text-slate-500 mt-1 max-w-md mx-auto">
            Enter the team you have today by Discipline × Channel × Level, or reuse a plan already
            saved on this client. Saved plans live on the client and can back many scenarios.
          </div>
          <div className="mt-3 flex items-center justify-center gap-2">
            <StaffPlanPicker
              plans={saved}
              linkedId={linkedPlanId}
              busy={attaching}
              label="Use a saved plan"
              onPick={attach}
            />
            {editHref && (
              <Link to={editHref} className="btn-primary inline-flex">
                <Users size={14} /> Enter current staff plan
              </Link>
            )}
          </div>
        </div>
      )}
      {attachError && (
        <div className="mt-2 text-xs text-rose-600">{attachError}</div>
      )}
    </CardShell>
  );
}

/** How a plan's numbers originated. Only an actual plan records the team
 *  a client really has, which is why it is the only kind a scenario can attach. */
function PlanKindPill({ plan }: { plan: Pick<StaffPlan, 'kind' | 'source' | 'entrySource'> }) {
  if (isActualStaffPlan(plan)) {
    const uploaded = staffPlanEntrySource(plan) === 'uploaded';
    return (
      <span
        className={clsx(
          'pill text-[10px] shrink-0',
          uploaded ? 'bg-sky-50 text-sky-800' : 'bg-slate-100 text-slate-600',
        )}
      >
        {staffPlanEntrySourceLabel(plan)}
      </span>
    );
  }
  if (plan.kind === 'final') {
    return (
      <span
        className="pill text-[10px] bg-emerald-50 text-emerald-700 shrink-0"
        title={`Post-automation headcount at ${staffPlanHorizonWords(
          plan.source?.horizon,
        )}`}
      >
        Final
      </span>
    );
  }
  const adjusted = plan.source?.adjustedCellCount || 0;
  return (
    <span className="pill text-[10px] bg-brand-purple-50 text-brand-purple-700 shrink-0">
      {adjusted > 0 ? 'Modeled-Adjusted' : 'Modeled'}
    </span>
  );
}

/**
 * Picks a saved staff plan for the scenario to compare against.
 *
 * Generated plans are listed but not selectable. Hub and OGS savings are measured
 * against the team a client really has, so attaching a plan the model produced
 * would compare the model with itself — the API rejects it outright, and showing
 * it greyed out with the reason beats letting the click fail.
 */
function StaffPlanPicker({
  plans,
  linkedId,
  busy,
  label,
  onPick,
  onClear,
}: {
  plans: StaffPlan[];
  linkedId?: string;
  busy: boolean;
  label: string;
  onPick: (id: string) => void;
  onClear?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  if (plans.length === 0) return null;

  const sorted = [...plans].sort((a, b) => {
    // Attachable plans first; the rest are context, not choices.
    const ea = isActualStaffPlan(a) ? 0 : 1;
    const eb = isActualStaffPlan(b) ? 0 : 1;
    if (ea !== eb) return ea - eb;
    return (b.updatedAt || '').localeCompare(a.updatedAt || '');
  });

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        className="btn-secondary shrink-0"
        onClick={() => setOpen((v) => !v)}
        disabled={busy}
        aria-expanded={open}
      >
        <Database size={14} /> {busy ? 'Loading…' : label}
        <ChevronDown size={13} className="text-slate-400" />
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-[340px] rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden">
          <div className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 border-b border-slate-100">
            Saved staff plans
          </div>
          <div className="max-h-[280px] overflow-y-auto py-1">
            {sorted.map((p) => {
              const attachable = isActualStaffPlan(p);
              const isLinked = p.id === linkedId;
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={!attachable || isLinked}
                  onClick={() => {
                    onPick(p.id);
                    setOpen(false);
                  }}
                  title={
                    attachable
                      ? undefined
                      : 'Generated from a model, so it cannot stand in for the team this client has today'
                  }
                  className={clsx(
                    'w-full text-left px-3 py-2 flex items-start gap-2',
                    attachable && !isLinked && 'hover:bg-slate-50 cursor-pointer',
                    (!attachable || isLinked) && 'cursor-default',
                  )}
                >
                  <div className="min-w-0 flex-1">
                    <div
                      className={clsx(
                        'text-xs font-medium truncate',
                        attachable ? 'text-slate-800' : 'text-slate-400',
                      )}
                    >
                      {p.name}
                    </div>
                    <div className="mt-0.5 flex items-center gap-1.5">
                      <PlanKindPill plan={p} />
                      {isLinked && (
                        <span className="pill text-[10px] bg-emerald-50 text-emerald-700">
                          current
                        </span>
                      )}
                    </div>
                    {!attachable && (
                      <div className="text-[10px] text-slate-400 mt-0.5">
                        Cannot be a current plan
                      </div>
                    )}
                  </div>
                  {p.updatedAt && (
                    <span className="text-[10px] text-slate-400 shrink-0 mt-0.5">
                      {new Date(p.updatedAt).toLocaleDateString()}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {onClear && linkedId && (
            <button
              type="button"
              onClick={() => {
                onClear();
                setOpen(false);
              }}
              className="w-full text-left px-3 py-2 text-xs text-rose-600 hover:bg-rose-50 border-t border-slate-100"
            >
              Detach current plan
            </button>
          )}
        </div>
      )}
    </div>
  );
}

